import { useEffect, useState } from "react";
import type { Me } from "@scrile/contracts";
import { describeError } from "../api/errors";
import { useApi } from "../api/context";
import { Link } from "../components/Link";
import { config } from "../config";
import { t, type MessageKey } from "../i18n";
import { formatDate, initial } from "../lib/format";
import { go, href, navigate, type Route } from "../lib/router";
import { usePlayerActions } from "../player/PlayerContext";
import { useHeroData } from "../state/heroData";
import { useToast } from "../state/toast";

type AccountRoute = Extract<Route, { name: "account" }>;
type Subscription = NonNullable<Me["subscription"]>;

/** Строка с датой: когда кончится пробный период, когда продлится, когда закончится. */
function periodLine(subscription: Subscription, timezone: string): string {
  const date = formatDate(subscription.currentPeriodEnd, timezone);
  if (subscription.status === "trialing") return t("account.trialEnds", { date });
  if (subscription.status === "canceled" || subscription.cancelAtPeriodEnd) return t("account.ends", { date });
  if (subscription.status === "past_due") return t("account.pastDue");
  return t("account.renews", { date });
}

const STATUS_KEYS: Record<Subscription["status"], MessageKey> = {
  trialing: "account.status.trialing",
  active: "account.status.active",
  past_due: "account.status.past_due",
  canceled: "account.status.canceled",
  paused: "account.status.paused",
};

/**
 * Аккаунт: кто вошёл, подписка на этого героя и выход. Управление подпиской — в
 * Stripe Customer Portal: отмена, карта и счета там, а не в нашем интерфейсе.
 */
export function Account({ route, me }: { route: AccountRoute; me: Me }) {
  const api = useApi();
  const { hero, setMe } = useHeroData();
  const actions = usePlayerActions();
  const toast = useToast();
  const [busy, setBusy] = useState<"portal" | "logout" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const subscription = me.subscription;

  useEffect(() => {
    if (!route.portal) return;
    if (config.mock) toast.show(t("account.portalMock"), 6_000);
    navigate(href.account(), { replace: true });
  }, [route.portal, toast]);

  async function openPortal() {
    setBusy("portal");
    setError(null);
    try {
      const { url } = await api.portal();
      go(url);
      setBusy(null);
    } catch (portalError) {
      setError(describeError(portalError));
      setBusy(null);
    }
  }

  async function signOut() {
    setBusy("logout");
    try {
      await api.logout();
    } catch {
      // Выход не должен застревать: локально забываем человека в любом случае
    }
    actions.stop();
    setMe(null);
    navigate(href.hero(), { replace: true });
  }

  return (
    <main className="container narrow account">
      <h1>{t("account.title")}</h1>

      <section className="panel account__profile">
        <span className="avatar avatar--letter" style={{ width: 56, height: 56, fontSize: 24 }} aria-hidden="true">
          {initial(me.displayName)}
        </span>
        <dl className="facts">
          <div>
            <dt>{t("account.name")}</dt>
            <dd>{me.displayName}</dd>
          </div>
          <div>
            <dt>{t("account.email")}</dt>
            <dd>{me.email}</dd>
          </div>
          <div>
            <dt>{t("account.timezone")}</dt>
            <dd>{me.timezone.replace(/_/g, " ")}</dd>
          </div>
        </dl>
      </section>

      <section className="panel account__subscription" aria-labelledby="subscription-title">
        <h2 id="subscription-title" className="panel__title">
          {t("account.subscription", { hero: hero.name })}
        </h2>
        {subscription ? (
          <>
            <div className="account__status">
              <span className={`status status--${subscription.status}`}>{t(STATUS_KEYS[subscription.status])}</span>
              <span>{t(subscription.plan === "yearly" ? "account.plan.yearly" : "account.plan.monthly")}</span>
            </div>
            <p className="account__period">{periodLine(subscription, me.timezone)}</p>
            {subscription.status === "trialing" && <p className="hint">{t("account.trialEndsReminder")}</p>}
            <button type="button" className="btn btn--outline btn--block" onClick={() => void openPortal()} disabled={busy !== null}>
              {busy === "portal" ? t("app.loading") : t("account.manage")}
            </button>
          </>
        ) : me.demoAccessUntil ? (
          <>
            <div className="account__status">
              <span className="status status--active">{t("account.demo.status")}</span>
              <span>{t("account.demo.plan")}</span>
            </div>
            <p className="account__period">{t("account.demo.ends", { date: formatDate(me.demoAccessUntil, me.timezone) })}</p>
            <p className="hint">{t("account.demo.noCharge")}</p>
          </>
        ) : (
          <>
            <p className="muted">{t("account.none", { hero: hero.name })}</p>
            <Link to={href.plans()} className="btn btn--primary btn--block">
              {t("account.seePlans")}
            </Link>
          </>
        )}
        {me.unlockedAll && !me.demoAccessUntil && <p className="notice notice--success">{t("account.unlocked")}</p>}
        {error && (
          <p className="form__error" role="alert">
            {error}
          </p>
        )}
      </section>

      <button type="button" className="btn btn--quiet btn--block" onClick={() => void signOut()} disabled={busy !== null}>
        {t("account.signOut")}
      </button>
    </main>
  );
}
